import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DropdownMenu, EmptyState, Spinner, TagGeneral, Text } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import ModelDetailsContent from '@/features/trainings/components/ModelDetailsContent';
import { modelDrawerDownloadBenchmark } from '@/features/trainings/components/modelDrawerTrainActions';
import { fetchTrainingsForConfigureModal } from '@/features/deployments/components/configureDeployment/fetchTrainingsForConfigureModal';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import TaskPageTabEmpty from '@/features/tasks/components/TaskPageTabEmpty';

/**
 * "Versions" task page tab: trained versions of the active objective,
 * with a version switcher and the shared `ModelDetailsContent` body
 * (Summary / Benchmarks / Data / Training) — the same content the
 * `ModelDetailsDrawer` shows from the Models page and Evaluation.
 */
const TaskModelDetailsTab = ({ activeUsecase }) => {
    const { role } = useAuth();
    const { allUsecases } = useContext(GlobalContext);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trainings, setTrainings] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState(null);

    const usecaseId = activeUsecase?._id || null;
    const clientId = activeUsecase?.client_id || DEFAULT_CLIENT_ID;

    useEffect(() => {
        if (!usecaseId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchTrainingsForConfigureModal({ allUsecases, clientId })
            .then((rows) => {
                if (cancelled) return;
                const own = (rows || []).filter(
                    (row) => row.llm_usecase_id === usecaseId
                );
                own.sort((a, b) => Number(b.version) - Number(a.version));
                setTrainings(own);
                setSelectedEventId(null);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || 'Failed to load trained versions.');
                setTrainings([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // allUsecases only enriches display fields; refetch on task switch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usecaseId, clientId]);

    const selectedTraining = useMemo(() => {
        if (!trainings.length) return null;
        if (selectedEventId) {
            const found = trainings.find(
                (t) => (t.distillation_event_id || t._id) === selectedEventId
            );
            if (found) return found;
        }
        // Default to the deployed version when there is one, else the latest.
        const deployed = trainings.find(
            (t) => t.is_deployed && Number(t.deployed_version) === Number(t.version)
        );
        return deployed || trainings[0];
    }, [trainings, selectedEventId]);

    const versionForContent = useMemo(() => {
        if (!selectedTraining) return null;
        const hasAnyDeployedVersion = trainings.some(
            (t) => t.is_deployed && t.deployed_version != null
        );
        return {
            ...selectedTraining,
            isThisVersionDeployed:
                Boolean(selectedTraining.is_deployed) &&
                Number(selectedTraining.deployed_version) === Number(selectedTraining.version),
            hasAnyDeployedVersion,
        };
    }, [selectedTraining, trainings]);

    if (!usecaseId) return null;

    if (loading && !trainings.length) {
        return (
            <div className="task-page-tab-loading" role="status" aria-label="Loading versions">
                <Spinner size="large" />
            </div>
        );
    }

    if (!trainings.length) {
        return (
            <TaskPageTabEmpty>
                <EmptyState
                    title="No trained versions yet"
                    subtitle={error || 'Train a model from Retrain in the task menu to see its versions here.'}
                    showSubtitle
                    showMainButton={false}
                />
            </TaskPageTabEmpty>
        );
    }

    const versionLabel = (t) => `V${t.version}`;
    const trainedOnAgreements = (t) => Boolean(t?.trained_on_label_agreements);

    // Always shown, even with a single version — the select then just displays
    // that version as the only option.
    const versionSelector = (
        <div className="task-model-details-tab__version fs-unmask">
            <Text variant="lead-semibold" color="primary" as="h2" className="task-model-details-tab__version-title">
                Versions
            </Text>
            <DropdownMenu
                size="medium"
                label="Version"
                showLabel
                placeholder="Version"
                text={selectedTraining ? versionLabel(selectedTraining) : ''}
                value={selectedTraining ? versionLabel(selectedTraining) : ''}
                items={trainings.map((t) => ({
                    label: versionLabel(t),
                    suffix: trainedOnAgreements(t) ? <TagGeneral label="Agreements" /> : undefined,
                }))}
                onSelect={(item, index) => {
                    const target = trainings[index];
                    if (target) {
                        setSelectedEventId(target.distillation_event_id || target._id);
                    }
                }}
            />
        </div>
    );

    return (
        <div className="task-model-details-tab">
            {/* No `key` on purpose: remounting on version change would reset
                the active sub-tab (Summary/Benchmarks/...). ModelDetailsContent
                refetches internally when distillationEventId changes. */}
            {versionForContent && (
                <ModelDetailsContent
                    distillationEventId={versionForContent.distillation_event_id}
                    version={versionForContent}
                    clientId={clientId}
                    role={role}
                    taskType={activeUsecase?.task_type}
                    onDownloadBenchmark={(training) => modelDrawerDownloadBenchmark(training)}
                    sideTabs
                    headerExtra={versionSelector}
                />
            )}
        </div>
    );
};

export default TaskModelDetailsTab;
